import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import { projectService } from '../../services/projectService';
import { demoProjectsService } from '../../services/demoProjectsService';
import { metersToFeet, slopeDegreesToPercentGrade } from '../../utils/units';
// detectStatCategory maps an info-panel artifact name to a terrain category, or
// null when the panel is not one of the terrain statistics panels. The three
// panels are named "Elevation/Slope/Aspect Statistics", so we require the
// "statistic" marker to avoid hijacking any other info panel.
export const detectStatCategory = (name) => {
    const lower = (name || '').toLowerCase();
    if (!lower.includes('statistic'))
        return null;
    if (lower.includes('aspect'))
        return 'aspect';
    if (lower.includes('slope'))
        return 'slope';
    if (lower.includes('geomorphon'))
        return 'geomorphons';
    if (lower.includes('elevation'))
        return 'elevation';
    return null;
};
const BORDER = 'var(--pewter)';
const TEXT = 'var(--charcoal)';
const MUTED = 'var(--pewter)';
// Proportion bar shown behind a percentage to give the table some visual weight.
const Bar = ({ percent, color }) => (_jsx("div", { className: "relative h-1.5 rounded-full overflow-hidden", style: { backgroundColor: 'rgba(0,0,0,0.08)' }, children: _jsx("div", { className: "absolute inset-y-0 left-0 rounded-full", style: { width: `${Math.max(0, Math.min(100, percent))}%`, backgroundColor: color || MUTED } }) }));
const Swatch = ({ color }) => (_jsx("span", { className: "inline-block w-3 h-3 rounded-sm flex-shrink-0", style: { backgroundColor: color || MUTED, border: '1px solid rgba(0,0,0,0.15)' } }));
// --- parsing helpers --------------------------------------------------------
// Slope items carry title "Flat (0 - 2°)" and description
// "0 - 2° (0.00 - 3.49%): Flat". We pull the class label, the degree range, and
// the percent-grade range out of those so we can show them as separate columns.
const parseSlopeRow = (item) => {
    var _a;
    const title = item.title || '';
    const desc = item.description || title;
    const label = title.replace(/\s*\(.*$/, '').trim() || title;
    const degMatch = desc.match(/(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)\s*°/);
    const degMin = degMatch ? parseFloat(degMatch[1]) : null;
    const degMax = degMatch ? parseFloat(degMatch[2]) : null;
    const openEnded = degMax !== null && degMax >= 90;
    let degrees;
    if (degMin !== null && degMax !== null) {
        degrees = openEnded ? `${degMin}°+` : `${degMin}–${degMax}°`;
    }
    else {
        degrees = title;
    }
    // Prefer the authoritative percent range from the description; fall back to
    // computing it from the degree bounds.
    const gradeRange = desc.match(/\(\s*([\d.]+)\s*[-–]\s*([\d.]+)\s*%\s*\)/);
    const gradeOpen = desc.match(/>\s*([\d.]+)\s*%/);
    let grade;
    if (gradeOpen) {
        grade = `${Math.round(parseFloat(gradeOpen[1]))}%+`;
    }
    else if (gradeRange) {
        grade = `${Math.round(parseFloat(gradeRange[1]))}–${Math.round(parseFloat(gradeRange[2]))}%`;
    }
    else if (degMin !== null && degMax !== null) {
        grade = openEnded
            ? `${Math.round(slopeDegreesToPercentGrade(degMin))}%+`
            : `${Math.round(slopeDegreesToPercentGrade(degMin))}–${Math.round(slopeDegreesToPercentGrade(degMax))}%`;
    }
    else {
        grade = '—';
    }
    // The structured description is "<deg> (<grade>): <explanation>"; keep just the
    // explanation ("Level terrain suitable for most land uses", etc.).
    const colon = desc.indexOf(': ');
    const note = colon >= 0 ? desc.slice(colon + 2).trim() : '';
    return { label, degrees, grade, note, percent: (_a = item.percent) !== null && _a !== void 0 ? _a : 0, color: item.color };
};
// --- category renderers -----------------------------------------------------
// classifyStdDev mirrors the backend thresholds (web-server ops/dem_processing/
// visualization/ops.go) so the terrain-character read stays consistent between
// the legacy info-panel HTML and this table. Each phrase completes the sentence
// "this area is …".
const classifyStdDev = (std) => {
    if (std < 10)
        return 'remarkably flat — nearly level ground, with only the gentlest of slopes throughout.';
    if (std < 25)
        return 'gently rolling, with soft hills and slight variation across the land.';
    if (std < 50)
        return 'pleasantly varied, with pronounced hills and ridges shaping the landscape.';
    if (std < 100)
        return 'rugged, with sharp elevation changes throughout.';
    return 'extremely rugged, with dramatic elevation swings from one end to the other.';
};
const ElevationTable = ({ stats, meanHeightM }) => {
    const { minHeightM, maxHeightM, altitudeStdDev } = stats;
    const range = typeof maxHeightM === 'number' && typeof minHeightM === 'number'
        ? maxHeightM - minHeightM
        : undefined;
    const rows = [
        { label: 'Minimum', m: minHeightM },
        { label: 'Maximum', m: maxHeightM },
        ...(typeof meanHeightM === 'number' ? [{ label: 'Mean', m: meanHeightM }] : []),
        { label: 'Range (Δ)', m: range },
        { label: 'Std deviation', m: altitudeStdDev },
    ];
    const fmt = (v, toFt) => typeof v === 'number' ? `${Math.round(toFt ? metersToFeet(v) : v).toLocaleString()} ${toFt ? 'ft' : 'm'}` : '—';
    return (_jsxs(_Fragment, { children: [_jsxs("table", { className: "w-full border-collapse text-sm", style: { color: TEXT }, children: [_jsx("thead", { children: _jsxs("tr", { style: { borderBottom: `1px solid ${BORDER}` }, children: [_jsx("th", { className: "text-left font-medium py-1.5 pr-2" }), _jsx("th", { className: "text-right font-medium py-1.5 px-2", style: { color: MUTED }, children: "Metric" }), _jsx("th", { className: "text-right font-medium py-1.5 pl-2", style: { color: MUTED }, children: "Imperial" })] }) }), _jsx("tbody", { children: rows.map((r) => (_jsxs("tr", { style: { borderBottom: `1px solid rgba(0,0,0,0.06)` }, children: [_jsx("td", { className: "py-1.5 pr-2", children: r.label }), _jsx("td", { className: "py-1.5 px-2 text-right tabular-nums", children: fmt(r.m, false) }), _jsx("td", { className: "py-1.5 pl-2 text-right tabular-nums", children: fmt(r.m, true) })] }, r.label))) })] }), typeof altitudeStdDev === 'number' && (_jsxs("p", { className: "text-[13px] leading-snug mt-2.5", style: { color: MUTED }, children: ["This standard deviation indicates that this area is ", classifyStdDev(altitudeStdDev)] }))] }));
};
// Elevation band titles arrive metric ("9 - 32m"). Render the metric range plus
// the converted imperial range so US users get feet too — matching the dual-unit
// summary above.
const formatElevationBand = (title) => {
    const t = title || '';
    const m = t.match(/(-?[\d.]+)\s*-\s*(-?[\d.]+)\s*m/i);
    if (!m)
        return { metric: t, imperial: '' };
    const lo = parseFloat(m[1]);
    const hi = parseFloat(m[2]);
    return {
        metric: `${m[1]} - ${m[2]} m`,
        imperial: `${Math.round(metersToFeet(lo))} - ${Math.round(metersToFeet(hi))} ft`,
    };
};
const ElevationBands = ({ items }) => {
    if (!items.length)
        return null;
    return (_jsxs("div", { className: "mt-4", children: [_jsx("div", { className: "text-xs font-semibold uppercase tracking-wide mb-2", style: { color: MUTED }, children: "By elevation band" }), _jsx("div", { className: "space-y-1.5", children: items.map((it, idx) => {
                    var _a, _b;
                    const band = formatElevationBand(it.title);
                    return (_jsxs("div", { className: "flex items-center gap-2 text-sm", style: { color: TEXT }, children: [_jsx(Swatch, { color: it.color }), _jsxs("span", { className: "flex-1 min-w-0 whitespace-nowrap", children: [band.metric, band.imperial && _jsxs("span", { className: "ml-1.5", style: { color: MUTED }, children: ["\u00B7 ", band.imperial] })] }), _jsx("div", { className: "w-16 flex-shrink-0", children: _jsx(Bar, { percent: (_a = it.percent) !== null && _a !== void 0 ? _a : 0, color: it.color }) }), _jsxs("span", { className: "w-12 text-right tabular-nums flex-shrink-0", style: { color: MUTED }, children: [((_b = it.percent) !== null && _b !== void 0 ? _b : 0).toFixed(1), "%"] })] }, it.key || idx));
                }) })] }));
};
const SlopeTable = ({ items }) => {
    const rows = useMemo(() => items.map(parseSlopeRow), [items]);
    return (_jsxs("table", { className: "w-full table-fixed border-collapse text-sm", style: { color: TEXT }, children: [_jsx("thead", { children: _jsxs("tr", { style: { borderBottom: `1px solid ${BORDER}` }, children: [_jsx("th", { className: "text-left font-medium py-1.5 pr-2 w-[46%]", style: { color: MUTED }, children: "Class" }), _jsx("th", { className: "text-right font-medium py-1.5 px-2 w-[18%]", style: { color: MUTED }, children: "Degrees" }), _jsx("th", { className: "text-right font-medium py-1.5 px-2 w-[18%]", style: { color: MUTED }, children: "% Grade" }), _jsx("th", { className: "text-right font-medium py-1.5 pl-2 w-[18%]", style: { color: MUTED }, children: "Area" })] }) }), _jsx("tbody", { children: rows.map((r, idx) => (_jsxs("tr", { style: { borderBottom: `1px solid rgba(0,0,0,0.06)` }, children: [_jsxs("td", { className: "py-1.5 pr-2 align-top", children: [_jsxs("span", { className: "flex items-center gap-2", children: [_jsx(Swatch, { color: r.color }), _jsx("span", { children: r.label })] }), r.note && (_jsx("div", { className: "text-[11px] leading-snug mt-0.5 ml-5", style: { color: MUTED }, children: r.note }))] }), _jsx("td", { className: "py-1.5 px-2 text-right tabular-nums whitespace-nowrap align-top", children: r.degrees }), _jsx("td", { className: "py-1.5 px-2 text-right tabular-nums whitespace-nowrap align-top", children: r.grade }), _jsxs("td", { className: "py-1.5 pl-2 text-right tabular-nums align-top", children: [r.percent.toFixed(1), "%"] })] }, idx))) })] }));
};
const AspectGrid = ({ items }) => (_jsx("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2", children: items.map((it, idx) => {
        var _a, _b;
        return (_jsxs("div", { className: "flex items-center gap-2 text-sm", style: { color: TEXT }, children: [_jsx(Swatch, { color: it.color }), _jsx("span", { className: "w-32 flex-shrink-0 whitespace-nowrap", children: it.title }), _jsx("div", { className: "flex-1 min-w-0", children: _jsx(Bar, { percent: (_a = it.percent) !== null && _a !== void 0 ? _a : 0, color: it.color }) }), _jsxs("span", { className: "w-12 text-right tabular-nums", style: { color: MUTED }, children: [((_b = it.percent) !== null && _b !== void 0 ? _b : 0).toFixed(1), "%"] })] }, it.key || idx));
    }) }));
const SimpleList = ({ items }) => (_jsx("div", { className: "space-y-1.5", children: items.map((it, idx) => {
        var _a, _b;
        return (_jsxs("div", { className: "flex items-center gap-2 text-sm", style: { color: TEXT }, children: [_jsx(Swatch, { color: it.color }), _jsx("span", { className: "flex-1 min-w-0 truncate", children: it.title }), _jsx("div", { className: "w-24", children: _jsx(Bar, { percent: (_a = it.percent) !== null && _a !== void 0 ? _a : 0, color: it.color }) }), _jsxs("span", { className: "w-12 text-right tabular-nums", style: { color: MUTED }, children: [((_b = it.percent) !== null && _b !== void 0 ? _b : 0).toFixed(1), "%"] })] }, it.key || idx));
    }) }));
// --- shell ------------------------------------------------------------------
export const StatisticsPanelContent = ({ category, projectId, isDemoProject, meanHeightM, }) => {
    const [stats, setStats] = useState(undefined);
    useEffect(() => {
        let cancelled = false;
        setStats(undefined);
        (async () => {
            try {
                const fetched = isDemoProject
                    ? await demoProjectsService.getProjectStatistics(projectId)
                    : await projectService.getProjectStatistics(projectId);
                if (!cancelled)
                    setStats(fetched || null);
            }
            catch (_a) {
                if (!cancelled)
                    setStats(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [projectId, isDemoProject]);
    if (stats === undefined) {
        return _jsx("div", { className: "py-6 text-center text-sm", style: { color: MUTED }, children: "Loading statistics\u2026" });
    }
    if (stats === null) {
        return _jsx("div", { className: "py-6 text-center text-sm", style: { color: MUTED }, children: "Statistics are not available for this project." });
    }
    let body = null;
    if (category === 'elevation') {
        body = (_jsxs(_Fragment, { children: [_jsx(ElevationTable, { stats: stats, meanHeightM: meanHeightM }), _jsx(ElevationBands, { items: stats.elevationBreakdown || [] })] }));
    }
    else if (category === 'slope') {
        body = _jsx(SlopeTable, { items: stats.slopeBreakdown || [] });
    }
    else if (category === 'aspect') {
        body = _jsx(AspectGrid, { items: stats.aspectBreakdown || [] });
    }
    else {
        body = _jsx(SimpleList, { items: stats.geomorphonsBreakdown || [] });
    }
    const empty = (category === 'slope' && !(stats.slopeBreakdown || []).length) ||
        (category === 'aspect' && !(stats.aspectBreakdown || []).length) ||
        (category === 'geomorphons' && !(stats.geomorphonsBreakdown || []).length) ||
        (category === 'elevation' &&
            typeof stats.minHeightM !== 'number' &&
            typeof stats.maxHeightM !== 'number' &&
            typeof stats.altitudeStdDev !== 'number' &&
            !(stats.elevationBreakdown || []).length);
    if (empty) {
        return _jsx("div", { className: "py-6 text-center text-sm", style: { color: MUTED }, children: "No data available for this layer." });
    }
    return _jsx("div", { children: body });
};
export default StatisticsPanelContent;
